import {useState, useEffect, useMemo, useCallback} from 'react';
import {PPDebitoDTO} from '../../../../../dtos/ProntoPagueiDTO';
import {parseBRValue} from '../utils';

export function useDebitSelection(todosDebitos: PPDebitoDTO[]) {
  const [selectedDebits, setSelectedDebits] =
    useState<PPDebitoDTO[]>(todosDebitos);

  const [totalSelectedValue, setTotalSelectedValue] = useState(0);

  const selectedSet = useMemo(
    () => new Set(selectedDebits),
    [selectedDebits],
  );

  useEffect(() => {
    const total = selectedDebits.reduce(
      (acc, item) => acc + parseBRValue(item.valor),
      0,
    );
    setTotalSelectedValue(total);
  }, [selectedDebits]);

  const allSelected = selectedDebits.length === todosDebitos.length;
  const someSelected = selectedDebits.length > 0 && !allSelected;
  const masterState: 'all' | 'some' | 'none' = allSelected
    ? 'all'
    : someSelected
    ? 'some'
    : 'none';

  const handleToggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedDebits(todosDebitos.filter(d => d.required));
    } else {
      setSelectedDebits(todosDebitos);
    }
  }, [allSelected, todosDebitos]);

  const handleSelectDebit = useCallback((item: PPDebitoDTO) => {
    if (item.required) {
      return;
    }

    const isSelected = selectedSet.has(item);

    setSelectedDebits(prev => {
      let next = [...prev];

      if (!isSelected) {
        item.depends.forEach(uid => {
          const dep = todosDebitos.find(d => d.uid === uid);
          if (dep && !next.find(d => d === dep)) {
            next.push(dep);
          }
        });

        item.distinct.forEach(uid => {
          const target = todosDebitos.find(d => d.uid === uid);
          if (target && !target.required) {
            next = next.filter(d => d.uid !== uid);
          }
        });

        item.onCheck.select.forEach(uid => {
          const found = todosDebitos.find(d => d.uid === uid);
          if (found && !next.find(d => d === found)) {
            next.push(found);
          }
        });
        item.onCheck.unSelect.forEach(uid => {
          const target = todosDebitos.find(d => d.uid === uid);
          if (!target?.required) {
            next = next.filter(d => d.uid !== uid);
          }
        });

        if (!next.find(d => d === item)) {
          next.push(item);
        }
      } else {
        todosDebitos.forEach(d => {
          if (d.depends.includes(item.uid) && !d.required) {
            next = next.filter(sel => sel !== d);
          }
        });

        item.onUnCheck.select.forEach(uid => {
          const found = todosDebitos.find(d => d.uid === uid);
          if (found && !next.find(d => d === found)) {
            next.push(found);
          }
        });
        item.onUnCheck.unSelect.forEach(uid => {
          const target = todosDebitos.find(d => d.uid === uid);
          if (!target?.required) {
            next = next.filter(d => d.uid !== uid);
          }
        });

        next = next.filter(d => d !== item);
      }

      return next;
    });
  }, [todosDebitos, selectedSet]);

  return {
    selectedDebits,
    selectedSet,
    totalSelectedValue,
    masterState,
    handleToggleAll,
    handleSelectDebit,
  };
}